import axiosInstance, { getAccessToken } from '../../../../lib/axios';

export type FeedbackStatus =
  | 'NEW'
  | 'TRIAGED'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_USER'
  | 'RESOLVED'
  | 'CLOSED'
  | 'SPAM';

export type FeedbackPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface FeedbackAttachment {
  filePublicId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  url: string;
}

export interface FeedbackMessage {
  id: number;
  senderType: 'USER' | 'ADMIN' | 'SYSTEM' | 'AGENT';
  senderId?: number | null;
  content: string;
  isAiGenerated: boolean;
  createdAt: string;
  attachments: FeedbackAttachment[];
}

export interface FeedbackTicket {
  publicId: string;
  title?: string | null;
  type: string;
  priority?: FeedbackPriority | null;
  status: FeedbackStatus;
  source?: string | null;
  meta: Record<string, unknown>;
  aiSummary?: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  user: {
    publicId: string;
    email: string;
    userName?: string | null;
    fullName?: string | null;
  };
  messages: FeedbackMessage[];
}

export interface FeedbackListResponse {
  feedback: FeedbackTicket[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FeedbackListParams {
  status?: string;
  type?: string;
  search?: string;
  page?: number;
  limit?: number;
}

const BASE_URL = '/v1/admin/feedback';

export const getAdminFeedback = async (
  params: FeedbackListParams
): Promise<FeedbackListResponse> => {
  const response = await axiosInstance.get<{ data: FeedbackListResponse }>(BASE_URL, { params });
  return response.data.data;
};

export const updateAdminFeedback = async (
  feedbackId: string,
  data: { status?: FeedbackStatus; priority?: FeedbackPriority }
): Promise<FeedbackTicket> => {
  const response = await axiosInstance.patch<{ data: FeedbackTicket }>(`${BASE_URL}/${feedbackId}`, data);
  return response.data.data;
};

export const replyToFeedback = async (
  feedbackId: string,
  content: string
): Promise<FeedbackTicket> => {
  const response = await axiosInstance.post<{ data: FeedbackTicket }>(
    `${BASE_URL}/${feedbackId}/replies`,
    { content }
  );
  return response.data.data;
};

export const getFeedbackEvidenceBlob = async (url: string): Promise<Blob> => {
  const response = await axiosInstance.get(url, { responseType: 'blob' });
  return response.data;
};

export const getAdminFeedbackWebSocketUrl = (): string | null => {
  const token = getAccessToken();
  const serverUrl = import.meta.env.VITE_SERVER_URL;
  if (!token || !serverUrl) return null;

  const url = new URL('/v1/admin/feedback/notifications/ws', serverUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  return url.toString();
};
